// Cruise's drive: hand flight on the frame's own seconds, and the delta-v map's
// GO flying it in the player's place, with the warp skip and the burn steps the
// autopilot runs inside a frame. The flight field, the ship's place and the
// flight step itself stay in src/main.ts for now and come in whole.
import * as THREE from 'three';
import { gameState, setAssistOn, setHeldCruiseThrottle } from '../state/gameState';
import {
  flightSubsteps, orbitSubstepS,
  type FlightAxes, type FlightConfig, type FlightState, type FlightVec,
} from '../sim/flight';
import type { FlightFrameContext } from '../sim/flightCentres';
import type { GravityField } from '../sim/gravityField';
import type { PathRecorder } from '../sim/pathRecorder';
import type { TrajectoryHandle } from '../sim/trajectory';
import type { BodyPlaces } from '../sim/bodies';
import type { AtlasWorldHandle } from '../sim/landVerb';
import type { WarpHandle } from '../sim/warp';
import type { SimPositionHandle } from '../sim/position';
import { GRAVITATIONAL_CONSTANT_M3_KG_S2 } from '../sim/constants';
import { bodyById } from '../world/bodies';
import type { BodyRow } from '../world/types';
import { heliocentric } from '../world/ephemeris';
import type { WorldVec } from '../world/frame';
import { gameClock, WARP_LEVELS } from '../world/clock';
import { createDvMap, type DvMap } from '../sim/dvMap';
import { correctionRowText, createDvCorrection, type DvCorrection } from '../sim/dvCorrection';
import type { TransferPlan } from '../sim/transfer';
import {
  createDvMapPanel, goOffered, loadArrivalOrbit, movedRowText, type DvMapFlight, type DvMapPanel,
} from '../hudDvMap';
import { nextStepLabel, planRows } from '../hudDvText';
import {
  BURN_STEPS_PER_FRAME, createAutopilot, SOLVE_MS_BUDGET, SOLVE_STEP_BUDGET,
} from '../sim/autopilot';
import type { Autopilot, AutopilotCommand, AutopilotEnv } from '../sim/autopilot';
import { capThrottle, type DriveLimitsFn } from '../sim/driveLimits';
import {
  AUTO_WARP_FIX_HOLD_S, AUTO_WARP_LEAD_REAL_S, AUTO_WARP_MIN_LEAD_REAL_S, AUTO_WARP_SHORT_LEAD_REAL_S, autoWarpEase, autoWarpTargetRate, BURN_WATCH_FIX_REAL_S, BURN_WATCH_REAL_S, burnWatchEase, burnWatchRate, loadAutoWarpOn, saveAutoWarpOn,
  WARP_SKIP_SUBSTEP_S, WARP_SKIP_TICK_S, warpSkipPressLeadS, warpSkipTickCoarse, warpSkipTickS,
} from './warpSkip';
import type { InputHandle } from '../input';
import type { Custos } from '../scene/custos';
import type { ThrusterDrive, Thrusters } from '../scene/thrusters';
import { track } from '../visits/bus';
import { createTripLog, type TripLog, type TripStart } from './tripLog';
import { TEST_SWITCHES } from '../urlFlags.ts';

/**
 * GO's own effect on the trip log: a trip still flying is closed first, with
 * `cancelled` - the outcome events.ts allows for a leg the player ended without
 * arriving - so one `trip_start` never owes two `trip_end`s. Exported because
 * this ordering is the drive's trip wiring, and the frame machinery around the
 * call site needs a live scene a unit test has not got.
 */
export function beginTrip(trips: TripLog, start: TripStart): void {
  trips.cancel();
  trips.begin(start);
}

/** What main.ts reassigns or shares with the rest of the flight step, read and written live. */
export interface CruiseDriveLive {
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
  readonly seedJd: number;
  flightSimSeconds: number;
  flightLastSubsteps: number;
  flightLastSubstepS: number;
}

export interface CruiseDriveDeps {
  scene: THREE.Scene;
  input: InputHandle;
  ship: Custos;
  thrusters: Thrusters;
  flight: FlightState;
  flightConfig: FlightConfig;
  simPos: SimPositionHandle;
  gravityField: GravityField;
  trajectory: TrajectoryHandle;
  pathRecorder: PathRecorder;
  warp: WarpHandle;
  live: CruiseDriveLive;
  /** A fight owns the stick and the clock. */
  inFight(): boolean;
  /** What the drive may do this frame; play answers from the heat budget. */
  driveLimits: DriveLimitsFn;
  /** Share of full thrust a trip GO starts from here flies at, which the
   *  delta-v map prices its burns on; play answers from the radiators. 1 where absent. */
  tripThrustShare?(): number;
  /** GO's own preparation outside the drive; play deploys the radiators. */
  deployForTrip(): void;
  /** GO's destination into the pilot panel's TARGET; false where the list does not offer it. */
  selectTarget?(id: string): boolean;
  rebaseFlightFrame(): void;
  advanceSimBodies(): void;
  frameParentStep(): FlightVec;
  stepFlightFrame(
    axes: FlightAxes, throttle: number, dt: number, attitudeDt: number,
    substeps: number, out: FlightVec, assistDt: number,
  ): void;
  noteGroundContact(): void;
  noteStopLateSag(dtS: number): void;
  notePathRow(): void;
  assistSeconds(dt: number, realDt: number): number;
  assistLive(): boolean;
  thrustLateral(dvTick: number, throttle: number, dt: number): number;
  syncShipTransform(): void;
  shipWorldAt(): WorldVec;
  shipWorldVelocity(): FlightVec;
  sampleTrajectoryBody(i: number, aheadS: number, out: WorldVec, vel: WorldVec): void;
  atlasWorld(): AtlasWorldHandle | null;
  _frameCtx: FlightFrameContext;
  _step: THREE.Vector3;
  _thrustDrive: ThrusterDrive;
  _thrustPush: THREE.Vector3;
  _invBubble: THREE.Quaternion;
  _flightQuat: THREE.Quaternion;
  FLIGHT_CARRY_OLD_PARENT: boolean;
  BODIES_PINNED: boolean;
  AUTOPILOT_KEEP_AIM: boolean;
  MAX_REAL_DT_S: number;
}

export interface CruiseDrive {
  /** One cruise frame of hand flight, or of the autopilot where GO has the stick. */
  fly(realDt: number, dt: number, t: number): void;
  /** A control touched outside the frame loop, which hands the stick back. */
  notePilotInput(): void;
  cancelAutopilot(): void;
  requestAutopilotWarp(rate: number, ceiling: number): void;
  autopilotFlightLine(): DvMapFlight | null;
  readonly dvMap: DvMap;
  readonly dvMapPanel: DvMapPanel;
  readonly dvCorrection: DvCorrection;
  readonly autopilot: Autopilot | null;
  readonly autopilotCmd: AutopilotCommand | null;
  /**
   * The clock instant the autopilot next has to fly something, or null with no
   * autopilot: the step it counts to, or the instant of its last line while a
   * burn is in hand. Its count is taken off a live frame, so behind the map,
   * where the autopilot does not run, this stays put while the clock moves.
   */
  readonly autopilotDueT: number | null;
  autopilotWarpWish: number | null;
  /** Written by the ASSIST switch, so a switch thrown mid-flight is what the hand-back restores. */
  autopilotAssistWas: boolean;
  /** The throttle handed to `stepFlight` last cruise frame, for the HUD fill. */
  readonly throttleApplied: number;
  dispose(): void;
}

export function createCruiseDrive(deps: CruiseDriveDeps): CruiseDrive {
  const {
    scene, input, ship, thrusters, flight, flightConfig, simPos, gravityField, trajectory,
    pathRecorder, warp, live, inFight, driveLimits, deployForTrip, selectTarget,
    rebaseFlightFrame, advanceSimBodies, frameParentStep, stepFlightFrame, noteGroundContact,
    noteStopLateSag, notePathRow, assistSeconds, assistLive, thrustLateral, syncShipTransform,
    shipWorldAt, shipWorldVelocity, sampleTrajectoryBody, atlasWorld,
    _frameCtx, _step, _thrustDrive, _thrustPush, _invBubble, _flightQuat,
    FLIGHT_CARRY_OLD_PARENT, BODIES_PINNED, AUTOPILOT_KEEP_AIM, MAX_REAL_DT_S,
  } = deps;
  const {
    field: _field, ground: _ground, pullIds: _pullIds, pullRadiiM: _pullRadii,
    pullPeriodS: _pullPeriodS, pullParentIdx: _pullParentIdx,
    update: updateFlightField, refill: refillFlightField,
  } = gravityField;

  // `?dvmapstale=1` freezes the delta-v map on its first plan, made from a
  // parking orbit at whatever body the ship booted over: a map that ignores both
  // the live state and the clock, which `descent-dojo.mjs --dvmap --inject
  // stale-plan` must catch. `?dvmapzeromass=1` takes every destination's mass
  // away in the context the map plans against, which is dvmap-check.mjs's own
  // zero-mass control surfacing through the displayed capture burn.
  // `?dvmapburnnow=1` prices every departure where the ship is this instant
  // instead of at the cheapest point of the orbit it is on, which is what
  // `--dvmap --inject burn-now` has to catch. `?dvmapplanej2000=1` takes a moon's
  // own orbit plane off its J2000 element fit instead of off the epoch the plan
  // is for, which moves Phobos's plane 1.1 degrees and is what `--dvmap --inject
  // plane-j2000` has to catch. `?dvcorrectionoff=1` blanks the COURSE FIX row and
  // hides its burn marker, which is the panel a hand pilot had before the row
  // existed and what `--transit --inject no-correction-row` has to catch. Nothing
  // a player reaches sets any of them.
  const DVMAP_STALE = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapstale') === '1';
  const DVMAP_ZERO_MASS = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapzeromass') === '1';
  const DVMAP_BURN_NOW = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapburnnow') === '1';
  const DVMAP_PLANE_J2000 = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapplanej2000') === '1';
  const DV_CORRECTION_OFF = TEST_SWITCHES && new URLSearchParams(location.search).get('dvcorrectionoff') === '1';
  // `?dvcorrectionholdoutlived=1` keeps the COURSE FIX row on the plan it was
  // picked on even after the clock has passed that plan's arrival, which blanks
  // the row for every trip flown after a window the map has walked on from, and
  // is what `--transit --inject hold-outlived-plan` has to catch.
  const DV_CORRECTION_HOLD_OUTLIVED = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('dvcorrectionholdoutlived') === '1';
  // `?dvcorrectionholdbyarrival=1` re-takes the COURSE FIX row's plan off the
  // held one's own arrival epoch alone, ignoring the map's hand-off, which is the
  // row as it read before a window that passed with nothing flown on it moved the
  // map's pick on, and is what `--transit --inject hold-by-arrival` has to catch.
  const DV_CORRECTION_HOLD_BY_ARRIVAL = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('dvcorrectionholdbyarrival') === '1';
  // `?dvmapresolvesarmed=1` re-solves the picked row under the player instead of
  // holding the crossing they armed on it, which hands a player who waits for the
  // window they were shown a different trip from the one they picked, and is what
  // `--transit --inject map-resolves-armed` has to catch.
  const DVMAP_RESOLVES_ARMED = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('dvmapresolvesarmed') === '1';
  // `?dvmapholdonlyonsurvey=1` leaves the armed hold to the surveys alone, so a
  // crossing armed from a parking orbit the ship has left is not given up until
  // the ship reaches another body, where the panel names it as a crossing that
  // left days ago; what `--transit --inject hold-only-on-survey` has to catch.
  const DVMAP_HOLD_ONLY_ON_SURVEY = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('dvmapholdonlyonsurvey') === '1';
  // `?dvmapunsliced=1` spends a whole survey in the one call that starts it,
  // which is the 620 ms frame the slicing exists to break up and what
  // `--dvmap --inject unsliced-survey` has to catch.
  const DVMAP_UNSLICED = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapunsliced') === '1';
  // `?dvmapimpulsedeparture=1` prices the departure as a single instant push
  // again, which is the figure a pilot could cut his burn on and still be in
  // orbit around the body he meant to leave.
  const DVMAP_IMPULSE_DEPARTURE = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapimpulsedeparture') === '1';
  // `?goorbitignore=1` prices and flies every arrival into LOW whatever ARRIVE IN
  // says, which `arrival-panel-check.mjs --inject ignore-orbit` has to catch.
  const GO_ORBIT_IGNORE = TEST_SWITCHES && new URLSearchParams(location.search).get('goorbitignore') === '1';

  // `?attitudesimdt=1` puts the sim dt (and its warp) back on the ship's own
  // rotation, reproducing the pre-fix bug where a tap or a drag turned the ship
  // as far as the clock scaled it. `tools/preview/descent-dojo.mjs --flight
  // --hand --inject attitude-simdt` is the only thing that sets it.
  const FLIGHT_ATTITUDE_SIM_DT = TEST_SWITCHES && new URLSearchParams(location.search).get('attitudesimdt') === '1';

  // `?lineepochlate=1`: the Atlas's bodies reach the frame's epoch only in
  // `render`, `line-end-ground-check.mjs --warp 960 --inject late-epoch`'s control.
  const LINE_EPOCH_LATE = TEST_SWITCHES
    && new URLSearchParams(location.search).get('lineepochlate') === '1';

  // ?gonoselect=1 / ?targetdrivesgo=1: autowarp-check --leg target's controls.
  const GO_NO_SELECT = TEST_SWITCHES && new URLSearchParams(location.search).get('gonoselect') === '1';
  const TARGET_DRIVES_GO = TEST_SWITCHES && new URLSearchParams(location.search).get('targetdrivesgo') === '1';

  /** The throttle actually handed to `stepFlight` last cruise frame, for the HUD fill. */
  let cruiseThrottleApplied = 0;

  // ---------- The delta-v map's Go ----------
  // `src/sim/autopilot.ts` owns attitude, throttle and the warp REQUEST from GO
  // until something ends it, through the player's own gates.
  let autopilot: Autopilot | null = null;
  /** The last line it gave, for the panel and the page handle. */
  let autopilotCmd: AutopilotCommand | null = null;
  /** Simulated seconds the clock already stood past the `jd` that line was given. */
  let autopilotCmdLagS = 0;
  /** The clock instant that line was given at: the end of the step it flew. */
  let autopilotCmdAtT = 0;
  /** The destination it took, which the map's own pick may move under it. */
  let autopilotDestName = '';
  /** The plan it is flying, held by reference so the panel's priced figure is the
   *  map's own and not a second copy of it. */
  let autopilotPlan: TransferPlan | null = null;
  /** The assist as the player had it: two controllers on one stick fight, so the
   *  autopilot flies with it off and puts it back as it was. */
  let autopilotAssistWas = true;
  /** The warp level the player had at GO. The autopilot asks for its own rates
   *  the whole flight, and every way out of it puts this one back. */
  let autopilotWarpWasIdx = 0;
  /** Simulated seconds a real second the player asked the clock for DURING the
   *  flight, or null while they have asked for nothing. The autopilot's own ask
   *  and this one are both ceilings: the clock runs at the lower, so a burn that
   *  cannot hold the rate keeps its own and the panel says which. */
  let autopilotWarpWish: number | null = null;
  /** GO's Auto-warp switch as the player left it, and whether a warp pick or a
   *  load has stood it down for the rest of the trip in hand. */
  let autoWarpOn = loadAutoWarpOn();
  let autoWarpStoodDown = false;
  /** The rate Auto-warp or a burn's watch pace last gave the clock, which the
   *  next frame's ease runs from; 0 while neither holds it. */
  let autopilotPaceRate = 0;
  /** The clock instant the burn in hand opened, null outside one: its length is
   *  what it has run plus what the guidance says it has left. */
  let burnOpenT: number | null = null;
  /** Simulated seconds a real second Auto-warp flies as ticks past `autoWarpClockCap`. */
  let autoWarpTickRate = 0;
  /** The step a WARP TO NEXT press was made for with Auto-warp not driving: the
   *  press lands short of it and the approach curve carries the clock the rest. */
  let skipCarry: string | null = null;
  /** Real seconds the approach in hand keeps the clock from its step. */
  let autoWarpLeadS = AUTO_WARP_LEAD_REAL_S;
  /** The clock instant and TDB date GO was pressed at: TRIP TIME's zero. */
  let tripStartT = 0;
  let tripStartJd = 0;
  /** The stick as it was at GO: a key already held is not a hand-back. Cleared on
   *  the first frame the controls read neutral. */
  let autopilotStickHeld = false;
  /** A control touched outside the frame loop - the throttle bar, the assist
   *  switch, a warp pick, a park verb. Consumed by the next cruise frame. */
  let autopilotPilotEvent = false;
  /** Longest `update()` call seen on a trim rung, ms: the before number for the
   *  sliced solve, thousands of integrator steps at a time. */
  let autopilotTrimUpdateMaxMs = 0;
  /** Longest whole frame seen on a trim rung, ms. */
  let autopilotTrimFrameMaxMs = 0;
  let autopilotTrimFrames = 0;
  /** Longest `update()` call of the flight, ms, whatever phase it came back in: a
   *  sliced solve holds the phase it was already in. */
  let autopilotUpdateMaxMs = 0;
  /** Frames whose command came back with a guidance solve still in flight. */
  let autopilotSolveFrames = 0;
  /** Widest the nose stood off its direction at the start of any of this frame's
   *  steps, radians. */
  let autopilotFrameNoseErrorRad = 0;
  // `?autopilotnohandback=1` ignores the stick-back path, the variant the transit
  // arm's no-handback control opens. Nothing a player reaches sets it.
  const AUTOPILOT_NO_HANDBACK = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('autopilotnohandback') === '1';
  // `?autopilotunslicedsolve=1` runs each solve whole inside the call that opens it,
  // the variant the transit arm's unsliced-solve control opens. No player reaches it.
  const AUTOPILOT_UNSLICED_SOLVE = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('autopilotunslicedsolve') === '1';
  // `?autopilotownplane=1` flies a moon departure that carries the plan's turn in
  // the ship's own plane, the moon-go arm's control. No player reaches it.
  const AUTOPILOT_OWN_PLANE = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('autopilotownplane') === '1';
  // `?capturepricedpush=1` flies the arrival burn unsteered until the plan's priced
  // push is spent, moon-arrival-coast's control. No player reaches it.
  const CAPTURE_PRICED_PUSH = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('capturepricedpush') === '1';
  // `?coorbitunrotated=1` matches a moon's orbit along the moon's own direction of
  // motion rather than the ship's own tangent. No player reaches it.
  const CO_ORBIT_UNROTATED = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('coorbitunrotated') === '1';
  // `?parkonmoonenergy=1` parks a match on the moon's own orbital energy rather
  // than on an orbit that opens away from it, moon-arrival-coast's control.
  const PARK_ON_MOON_ENERGY = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('parkonmoonenergy') === '1';

  // `?burnholdsrate=1` asks for the rate one unsplit frame of a burn buys and
  // never splits one, the variant the transit arm's burn-holds-rate control opens.
  const BURN_HOLDS_RATE = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('burnholdsrate') === '1';
  // `?burnfrozennose=1` hands a slewing frame back the unsplit rate and no step,
  // the variant the transit arm's burn-frozen-nose control opens. No player
  // reaches it.
  const BURN_FROZEN_NOSE = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('burnfrozennose') === '1';
  // `?slewinoneframe=1` turns a burn's nose on a split frame's simulated seconds
  // with the clock free to rise under it, burn-slew-check's one-frame-turn control.
  const SLEW_IN_ONE_FRAME = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('slewinoneframe') === '1';

  // ---------- Warp to the next step ----------
  // A window two years out is twelve real minutes of sitting at a day a second,
  // and the last week of it is slower again because the ladder drops to an hour a
  // second inside eight real seconds of the burn. The skip runs those same cruise
  // ticks as fast as the frame will carry them instead of one a frame. Its leads,
  // tick and substep are `warpSkip.ts`'s.
  /** Real milliseconds one animation frame spends on the skip. Spread over frames
   *  rather than run in one call so the page paints the clock running through it. */
  const WARP_SKIP_FRAME_MS = 120;
  // `?warpskipnolead=1` aims the skip at the step itself instead of a day short of
  // it, so the clock lands on the window with no time left to fly it. The transit
  // arm's skip-no-lead control opens it; nothing a player reaches sets it.
  const WARP_SKIP_NO_LEAD = TEST_SWITCHES && new URLSearchParams(location.search)
    .get('warpskipnolead') === '1';
  // autowarp-check's controls, one each: Auto-warp holds a day a second into the
  // step, ignores a warp pick, drops the arrival approach's rate in one frame, or leaves
  // its switch unstored. Nothing a player reaches sets any of them.
  const autoWarpSwitch = (name: string): boolean => TEST_SWITCHES
    && new URLSearchParams(location.search).get(name) === '1';
  const AUTO_WARP_NO_DROP = autoWarpSwitch('autowarpnodrop');
  const AUTO_WARP_IGNORE_PLAYER = autoWarpSwitch('autowarpignoreplayer');
  const AUTO_WARP_CUT = autoWarpSwitch('autowarpcut');
  const AUTO_WARP_NO_PERSIST = autoWarpSwitch('autowarpnopersist');
  // The position-jump control: once, well into the arrival coast, the ship flies four
  // frames of ticks the clock does not count.
  const AUTO_WARP_JUMP = autoWarpSwitch('autowarpjump');
  let autoWarpJumpFrames = 0;
  // The stall arm's control: every course fix solved at its date, the clock standing still.
  const SOLVE_AT_DATE = autoWarpSwitch('solveatdate');
  // The wait leg's control: the parked wait's clock share held at the top rate.
  const AUTO_WARP_SLOW_WAIT = autoWarpSwitch('autowarpslowwait');
  /** Real milliseconds one frame gives Auto-warp's ticks past the clock's own share: a
   *  frame over it paces the clock slower rather than dropping the frame rate. */
  const AUTO_WARP_FRAME_MS = 20;
  const WARP_TOP_RATE = WARP_LEVELS[WARP_LEVELS.length - 1]!.rate;
  /** The parked wait's clock share: at 1 DAY/S the 2 s flight substep ate most of the frame and starved the ticks. */
  const AUTO_WARP_WAIT_CLOCK_RATE = 3600;
  /** The most of Auto-warp's rate the clock carries itself, the rest flown as ticks. */
  let autoWarpClockCap = WARP_TOP_RATE;
  /** The same flight, integrated at the skip's own coarse substep. Only the skip
   *  ticks run under this; every frame a player flies runs under `flightConfig`. */
  const warpSkipFlightConfig: FlightConfig = {
    ...flightConfig,
    maxSubstepS: WARP_SKIP_SUBSTEP_S,
    maxSubsteps: Math.ceil(WARP_SKIP_TICK_S / WARP_SKIP_SUBSTEP_S),
  };

  /** The skip's substep off the field as it stands: `orbitSubstepS` about the frame's
   *  parent, save where the ship's place round it decides nothing yet. */
  function warpSkipSubstepS(phaseFree: boolean): number {
    const parent = _field.pulls[gravityField.frameParentIdx];
    if (phaseFree || parent === undefined) return WARP_SKIP_SUBSTEP_S;
    const at = _field.at;
    return orbitSubstepS(WARP_SKIP_SUBSTEP_S, parent.gm,
      Math.hypot(at.x - parent.at.x, at.y - parent.at.y, at.z - parent.at.z));
  }

  /** The stick and the throttle keys as the frame loop reads them. */
  function stickTouched(): boolean {
    const s = input.state;
    return s.pitch !== 0 || s.yaw !== 0 || s.roll !== 0 || s.throttleKey || s.allStop;
  }

  // A control touched outside the frame loop: the autopilot hands the stick back on
  // the next cruise frame, the same frame the input itself is flown on.
  function notePilotInput(): void {
    if (autopilot !== null) autopilotPilotEvent = true;
  }

  // The registry as the guidance's own lookup: `heliocentric()` is the placement
  // the pull set and the Atlas draw from, in kilometres, against metres here.
  const autopilotEnv: AutopilotEnv = {
    bodyAt(id: string, jd: number, out: FlightVec): void {
      const row = bodyById(live.bodyRows, id);
      if (!row) { out.x = 0; out.y = 0; out.z = 0; return; }
      const st = heliocentric(row, live.bodyRows, jd);
      out.x = st.position.x * 1000;
      out.y = st.position.y * 1000;
      out.z = st.position.z * 1000;
    },
    bodyVelocityAt(id: string, jd: number, out: FlightVec): void {
      const row = bodyById(live.bodyRows, id);
      if (!row) { out.x = 0; out.y = 0; out.z = 0; return; }
      const st = heliocentric(row, live.bodyRows, jd);
      out.x = st.velocity.x * 1000;
      out.y = st.velocity.y * 1000;
      out.z = st.velocity.z * 1000;
    },
    gmOf(id: string): number {
      const row = bodyById(live.bodyRows, id);
      return row ? GRAVITATIONAL_CONSTANT_M3_KG_S2 * row.mass_kg : 0;
    },
    radiusOf(id: string): number {
      const row = bodyById(live.bodyRows, id);
      return row ? row.radius_m : 0;
    },
  };

  const _apAt: FlightVec = { x: 0, y: 0, z: 0 };
  const _apVel: FlightVec = { x: 0, y: 0, z: 0 };
  /** The frame a trim rung ran on is as long as the NEXT frame's `realDt` says. */
  let autopilotTrimFramePending = false;

  /** The trip's own two events: one `trip_start` at GO, one `trip_end` at the
   *  line that ends it, with the days, the real seconds and the skips it took. */
  const trips = createTripLog({
    track,
    simSeconds: () => gameClock().t,
    realMs: () => performance.now(),
  });

  /** A visit that ends mid-trip still owes the leg its `trip_end`: this runs on
   *  the drive's tear-down path, ahead of the visit's own last event. */
  const onPageHide = (): void => { trips.cancel(); };
  window.addEventListener('pagehide', onPageHide);

  /** Fly the row picked on the map. False where there is nothing to fly. */
  function startAutopilot(): boolean {
    if (inFight()) return false;
    const plan = dvMap.selected();
    if (plan === null) return false;
    // The panel offers GO on exactly the plans the guidance has a departure law
    // for; this is that same answer off the button.
    if (!goOffered(plan) || dvMap.state().arrivalRepricing) return false;
    // A GO that supersedes a trip still flying closes that pair before the new one.
    beginTrip(trips, { plan, originId: dvMap.state().survey?.originBodyId ?? 'none' });
    endAutopilot();
    autopilotCmd = null;
    autopilotDestName = plan.destinationName;
    autopilotTrimUpdateMaxMs = 0;
    autopilotTrimFrameMaxMs = 0;
    autopilotTrimFrames = 0;
    autopilotUpdateMaxMs = 0;
    autopilotSolveFrames = 0;
    autopilotTrimFramePending = false;
    autopilotPilotEvent = false;
    autopilotAssistWas = gameState().ship.flight.assistOn;
    autopilotWarpWasIdx = warp.pickIdx;
    autopilotWarpWish = null;
    tripStartT = gameClock().t;
    tripStartJd = gameClock().jdTdb();
    // A key or stick held from before GO is what the player was flying with, not a
    // hand-back: the touch only counts once the controls have read neutral once.
    autopilotStickHeld = stickTouched();
    setAssistOn(false);
    deployForTrip();
    autopilot = createAutopilot(plan, {
      accelMps2: flightConfig.accel,
      turnRateRadS: flightConfig.turnRate,
      warpRates: WARP_LEVELS.map((l) => l.rate),
      // Frames a real second the warp rules may price a burn's frame at: `realDt`
      // is clamped, so one frame flies up to MAX_REAL_DT_S times the rate.
      framesPerSecond: 1 / MAX_REAL_DT_S,
      // The frame loop never runs a whole guidance solve: a rung is 5,000 to 40,000
      // integrator steps and one call gets a slice of them.
      solveStepBudget: AUTOPILOT_UNSLICED_SOLVE ? 0 : SOLVE_STEP_BUDGET,
      solveMsBudget: AUTOPILOT_UNSLICED_SOLVE ? 0 : SOLVE_MS_BUDGET,
      burnHoldsRate: BURN_HOLDS_RATE,
      burnFrozenNose: BURN_FROZEN_NOSE,
      slewInOneFrame: SLEW_IN_ONE_FRAME,
      departureInOwnPlane: AUTOPILOT_OWN_PLANE,
      capturePricedPush: CAPTURE_PRICED_PUSH,
      coOrbitUnrotated: CO_ORBIT_UNROTATED,
      parkOnMoonEnergy: PARK_ON_MOON_ENERGY,
    }, autopilotEnv);
    autopilotPlan = autopilot.plan;
    if (!GO_NO_SELECT) selectTarget?.(plan.destinationId);
    return true;
  }

  /** Let go of the stick and put the assist and the warp back as the player had
   *  them. Every way out goes through here: hand-back, refusal, arrival, cancel. */
  function endAutopilot(): void {
    warp.clearSkip();
    if (autopilot === null) return;
    // A trip Auto-warp paced to its end hands back at real time, easing onto it.
    const paced = autoWarpDriving();
    autopilot = null;
    setAssistOn(autopilotAssistWas);
    // The direction the trip's own turns left on the state is wherever its last
    // brake pushed the bow, in the frame parent's velocity rather than the
    // ship's. A null aim is the ship's own velocity: nothing for the assist to
    // buy on any way out of here until the player commands a direction.
    if (!AUTOPILOT_KEEP_AIM) {
      flight.assistAim = null;
      flight.assistCatchUp = false;
    }
    autopilotStickHeld = false;
    autopilotWarpWish = null;
    autoWarpStoodDown = false;
    autopilotPaceRate = 0;
    burnOpenT = null;
    autoWarpTickRate = 0;
    skipCarry = null;
    warp.setPick(paced ? 0 : autopilotWarpWasIdx, false);
    warp.paint();
  }

  /** Whether Auto-warp has the clock: switched on, a trip flying, and no warp
   *  pick, load or pin since GO. */
  function autoWarpDriving(): boolean {
    return autoWarpOn && !autoWarpStoodDown && autopilot !== null
      && (autopilotWarpWish === null || AUTO_WARP_IGNORE_PLAYER) && !warp.pinned;
  }

  /** Whether the approach curve has the clock: Auto-warp, or a WARP TO NEXT press
   *  carried on from where its skip landed. */
  function autoPaced(): boolean {
    return autoWarpDriving() || (skipCarry !== null && !warp.skipping);
  }

  function autoWarpStatus(): 'driving' | 'stood down' | 'off' {
    if (!autoWarpOn) return 'off';
    return autoWarpDriving() ? 'driving' : 'stood down';
  }

  /** A warp pick or a load mid-trip: the clock is the player's until the trip ends. */
  function standDownAutoWarp(): void {
    skipCarry = null;
    if (AUTO_WARP_IGNORE_PLAYER || !autoWarpDriving()) return;
    autoWarpStoodDown = true;
    autopilotPaceRate = 0;
    autoWarpTickRate = 0;
  }

  function setAutoWarpOn(on: boolean): void {
    autoWarpOn = on;
    if (!AUTO_WARP_NO_PERSIST) saveAutoWarpOn(on);
    // Switching it back on mid-trip hands it the clock again, a warp pick and all.
    if (on && autopilot !== null) {
      autoWarpStoodDown = false;
      autopilotWarpWish = null;
    }
    autopilotPaceRate = 0;
    autoWarpTickRate = 0;
    warp.paint();
  }

  const AUTO_WARP_STEP_WORDS: Record<string, string> = {
    window: 'THE DEPARTURE WINDOW', 'course-fix': 'THE COURSE FIX', arrival: 'THE ARRIVAL',
  };

  /** The warp control's standing line while GO flies with Auto-warp switched on. */
  function autoWarpLine(): string | null {
    if (autopilot === null || !autoWarpOn) return null;
    if (!autoWarpDriving()) return 'AUTO-WARP OFF FOR THIS TRIP - THE CLOCK IS YOURS';
    const cmd = autopilotCmd;
    if (cmd === null) return 'AUTO-WARP';
    if (cmd.burnStepS > 0) return 'AUTO-WARP: PLAYING THE BURN OUT';
    if (cmd.solving === true || (cmd.nextStep === 'course-fix' && cmd.fixSolved !== true
      && autopilotNextEventS(cmd) <= AUTO_WARP_FIX_HOLD_S)) return 'AUTO-WARP: WORKING OUT THE COURSE FIX';
    const step = cmd.nextStep === null ? null : AUTO_WARP_STEP_WORDS[cmd.nextStep] ?? null;
    return step === null ? 'AUTO-WARP' : `AUTO-WARP TO ${step}`;
  }

  // A carried WARP TO NEXT paces off the plan's count too, so the approach ceiling and NEAR drop stand aside for it.
  warp.bindAutoWarp({ driving: autoPaced, line: autoWarpLine, standDown: standDownAutoWarp });

  /** The CANCEL button, and anything else that ends it with no input to blame. */
  function cancelAutopilot(): void {
    const ap = autopilot;
    if (ap === null) { autopilotCmd = null; return; }
    ap.cancel('you cancelled the trip');
    // One more call so the panel's last line is the autopilot's own, not a
    // guess this file made about what cancelling looks like.
    autopilotCmd = ap.update({
      jd: gameClock().jdTdb(),
      dtSimS: 0,
      attitudeDtS: 0,
      state: flight,
      at: _apAt,
      velocity: _apVel,
      pilotInput: false,
    });
    trips.cancel();
    endAutopilot();
  }

  // The autopilot's warp ask through the player's own button and ceiling: an ask
  // over warp.ceilingIdx() is clamped to that ceiling rather than dropped. A rate
  // the player asked for mid-flight REPLACES the autopilot's ask up to what the
  // trip can hold, so a burn left alone runs at the pace it always did and one a
  // player hurries runs as fast as the guidance can still steer it.
  function requestAutopilotWarp(rate: number, ceiling: number): void {
    // Auto-warp and a burn's watch pace set the clock once a frame, in paceAutopilotClock.
    if (autoPaced() || (autopilotWarpWish === null && burnOpenT !== null)) return;
    const want = autopilotWarpWish === null ? rate : Math.min(autopilotWarpWish, ceiling);
    let idx = 0;
    for (let i = 0; i < WARP_LEVELS.length; i++) {
      if ((WARP_LEVELS[i]?.rate ?? Infinity) <= want + 1e-6) idx = i;
    }
    warp.setPick(Math.min(idx, warp.ceilingIdx()), false);
    warp.paint();
  }

  // One call a frame, before the ship is stepped: the axes and throttle this frame
  // flies in place of the pilot's, or null where nothing is flying.
  function updateAutopilot(
    dtSimS: number, attitudeDtS: number, stick: boolean, framed = true, slewRealS = attitudeDtS,
  ): AutopilotCommand | null {
    const ap = autopilot;
    if (ap === null) return null;
    // A burn step and a skip tick are not frames, and their attitude seconds are
    // simulated ones: charging either to the trim frame's cost reads as a frame
    // hours long.
    if (autopilotTrimFramePending && framed) {
      const frameMs = attitudeDtS * 1000;
      if (frameMs > autopilotTrimFrameMaxMs) autopilotTrimFrameMaxMs = frameMs;
      autopilotTrimFramePending = false;
    }
    const pilotInput = (stick || autopilotPilotEvent) && !AUTOPILOT_NO_HANDBACK;
    autopilotPilotEvent = false;
    // The state's velocity is relative to the frame's parent; the guidance is
    // heliocentric, and the two differ by a velocity and not by a rotation.
    const frameParentId = gravityField.frameParentId;
    const parentV = frameParentId === null ? null : live.simBodies?.velocityAt(frameParentId) ?? null;
    _apVel.x = flight.velocity.x + (parentV?.x ?? 0);
    _apVel.y = flight.velocity.y + (parentV?.y ?? 0);
    _apVel.z = flight.velocity.z + (parentV?.z ?? 0);
    const w = shipWorldAt();
    _apAt.x = w.x;
    _apAt.y = w.y;
    _apAt.z = w.z;
    const limits = driveLimits(true);
    const t0 = performance.now();
    // Every caller has already moved the clock to the END of the step while the
    // ship and the registry still stand at its start, which is what `jd` names.
    const cmd = ap.update({
      jd: gameClock().jdTdb() - dtSimS / 86400,
      dtSimS,
      attitudeDtS,
      slewRealS,
      solveAhead: framed && !SOLVE_AT_DATE,
      state: flight,
      at: _apAt,
      velocity: _apVel,
      pilotInput,
      throttleCap: limits.throttleCap,
      burnThrustShare: limits.burnThrustShare,
    });
    const updateMs = performance.now() - t0;
    autopilotCmd = cmd;
    autopilotCmdLagS = dtSimS;
    autopilotCmdAtT = gameClock().t;
    if (updateMs > autopilotUpdateMaxMs) autopilotUpdateMaxMs = updateMs;
    if (cmd.noseErrorRad > autopilotFrameNoseErrorRad) autopilotFrameNoseErrorRad = cmd.noseErrorRad;
    if (cmd.solving === true) autopilotSolveFrames += 1;
    if (cmd.phase === 'trim') {
      autopilotTrimFrames += 1;
      autopilotTrimFramePending = true;
      if (updateMs > autopilotTrimUpdateMaxMs) autopilotTrimUpdateMaxMs = updateMs;
    }
    trips.note(cmd);
    if (cmd.done) endAutopilot();
    else requestAutopilotWarp(cmd.warpRate, cmd.warpCeiling);
    return cmd;
  }

  /** Simulated seconds the skip would take off the wait: the count to the
   *  autopilot's next dated step, less the lead it stops short by. 0 where there
   *  is no step ahead, or the step is inside the lead or within a second of it. */
  function warpSkipSeconds(): number {
    const cmd = autopilotCmd;
    if (autopilot === null || cmd === null || cmd.nextStep === null || autoPaced()) return 0;
    const next = autopilotNextEventS(cmd);
    const gap = next - (WARP_SKIP_NO_LEAD ? 0 : warpSkipPressLeadS(next, cmd.turnLeadS ?? 0));
    return Number.isFinite(gap) && gap > 1 ? gap : 0;
  }

  /** Simulated seconds from the clock to the autopilot's next dated step. Its own
   *  count runs from the start of the step it was handed, the clock from the end. */
  function autopilotNextEventS(cmd: AutopilotCommand): number {
    return Math.max(0, cmd.nextEventS - autopilotCmdLagS);
  }

  /** Arm the skip. The ticks run in the cruise branch, not here: a press happens
   *  outside the frame loop and the sim advances inside it. */
  function startWarpSkip(): void {
    const seconds = warpSkipSeconds();
    if (seconds <= 0 || autopilotCmd === null) return;
    warpSkipLeadS = autopilotNextEventS(autopilotCmd) - seconds;
    warpSkipFrameMaxMs = 0;
    warp.startSkip(gameClock().t + seconds);
    skipCarry = autopilotCmd.nextStep;
    trips.skipped();
  }

  /** The lead the skip was pressed for, which it keeps if the step moves under it. */
  let warpSkipLeadS = 0;
  /** Real milliseconds the slowest frame of the skip in hand spent on its ticks. */
  let warpSkipFrameMaxMs = 0;

  /**
   * One skip tick: the cruise branch's own sim work at `dtSimS` simulated
   * seconds, in the order the frame loop runs it, with no pilot input and no
   * throttle. False where the autopilot has stopped waiting and the skip is over.
   *
   * The clock is never moved past this - each tick integrates the ship and places
   * the registry for the seconds it just added, so a skipped wait leaves the same
   * orbit, the same body positions and the same plan a flown one would.
   */
  function warpSkipTick(dtSimS: number): boolean {
    const cmd = updateAutopilot(dtSimS, 0, false, false);
    if (cmd === null || cmd.nextStep === null || cmd.solving === true) return false;
    updateFlightField();
    rebaseFlightFrame();
    advanceSimBodies();
    const carried = frameParentStep();
    simPos.advance(carried.x, carried.y, carried.z);
    refillFlightField();
    // The coarse substep is the whole saving: `stepFlight` sizes its own steps off
    // the config's `maxSubstepS`, so the count passed to the span loop cannot
    // lengthen one. Put back before anything a player flies reads it.
    const substepS = warpSkipSubstepS(cmd.departureUntimed === true);
    warpSkipFlightConfig.maxSubstepS = substepS;
    warpSkipFlightConfig.maxSubsteps = Math.max(1, Math.ceil(dtSimS / substepS));
    _frameCtx.config = warpSkipFlightConfig;
    live.flightLastSubsteps = flightSubsteps(warpSkipFlightConfig, dtSimS, _field.pulls.length, false);
    live.flightLastSubstepS = live.flightLastSubsteps > 0 ? dtSimS / live.flightLastSubsteps : 0;
    _ground.contact.hit = false;
    stepFlightFrame(cmd.axes, 0, dtSimS, 0, live.flightLastSubsteps, _step, 0);
    _frameCtx.config = flightConfig;
    noteGroundContact();
    simPos.advance(_step.x, _step.y, _step.z);
    live.flightSimSeconds += dtSimS;
    return true;
  }

  /** Run the skip for this frame's share of it. A touched control, a cancelled
   *  flight or a guidance solve ends it where it stands. */
  function runWarpSkip(stick: boolean, frameDtS: number): void {
    const target = warp.skipTargetT;
    if (target === null) return;
    const ap = autopilot;
    if (ap === null || stick || inFight()) { warp.clearSkip(); return; }
    // The clock already carries this frame's seconds, which the ship has not flown
    // yet; ticks run on top of it hand the autopilot an epoch a frame past the state.
    gameClock().scrub(-frameDtS);
    const started = performance.now();
    try {
      skipTicks(ap, target - frameDtS, warpSkipLeadS + frameDtS);
    } finally {
      gameClock().scrub(frameDtS);
    }
    warpSkipFrameMaxMs = Math.max(warpSkipFrameMaxMs, performance.now() - started);
  }

  /** `leadS` is where the step's own count stops, with this frame's seconds on top
   *  since the frame flies them after the ticks. */
  function skipTicks(
    ap: Autopilot, target: number, leadS: number, budgetS = Infinity, frameMs = WARP_SKIP_FRAME_MS,
  ): void {
    const started = performance.now();
    let lastSpent = 0;
    let ticks = 0;
    let flownS = 0;
    for (;;) {
      // The departure's start is timed on the way in and can move, so the landing
      // follows the step's own count rather than the date the press aimed at.
      const cmd = autopilotCmd;
      const left = cmd === null || cmd.nextStep === null
        ? target - gameClock().t
        : autopilotNextEventS(cmd) - leadS;
      if (!(left > 0)) { warp.clearSkip(); return; }
      if (ap.freezes(gameClock().jdTdb())) return;
      // A tick costs a coarse one's substeps however fine they are, and the landing
      // tick gets a frame of its own, or the frame after it flies this one's wall time at warp.
      const coarse = cmd !== null
        && warpSkipTickCoarse(cmd.departureUntimed === true, autopilotNextEventS(cmd), cmd.turnLeadS ?? 0, cmd.timingReachS);
      const tick = Math.min(warpSkipTickS(warpSkipSubstepS(coarse)), left, budgetS - flownS);
      if (!(tick > 0)) return;
      if (tick === left && ticks > 0) return;
      ticks++;
      gameClock().scrub(tick);
      const ok = warpSkipTick(tick);
      const spent = performance.now() - started;
      warp.addSkipTick(spent - lastSpent);
      lastSpent = spent;
      if (!ok) { gameClock().scrub(-tick); warp.clearSkip(); return; }
      flownS += tick;
      if (spent >= frameMs || flownS >= budgetS) return;
    }
  }

  /** Auto-warp's share of this frame past the clock's top rate, flown as skip
   *  ticks, stopping where its pace comes back under that rate. */
  function runAutoWarpTicks(stick: boolean, frameDtS: number, realDt: number): void {
    const ap = autopilot;
    const cmd = autopilotCmd;
    const budgetS = autoWarpTickRate * realDt;
    if (AUTO_WARP_JUMP && ap !== null && cmd !== null && cmd.nextStep === 'arrival' && cmd.burnStepS <= 0
      && autoPaced() && !stick && !inFight() && !warp.skipping && autoWarpJumpFrames >= 0) {
      const toStepS = autopilotNextEventS(cmd);
      if (autoWarpJumpFrames === 0) autoWarpJumpFrames = toStepS;
      else if (toStepS < autoWarpJumpFrames * 0.1) {
        autoWarpJumpFrames = -1;
        const gc = gameClock();
        const t0 = gc.t;
        skipTicks(ap, Infinity, 0, 20 * frameDtS, AUTO_WARP_FRAME_MS);
        gc.scrub(t0 - gc.t);
        return;
      }
    }
    if (ap === null || cmd === null || cmd.nextStep === null || !(budgetS > 0)) return;
    if (stick || inFight() || warp.skipping || !autoPaced()) return;
    const gc = gameClock();
    if (gc.paused || gc.held) return;
    gc.scrub(-frameDtS);
    try {
      // The parked wait gets WARP TO NEXT's frame: the orbit is a blur at its rates whatever the frame rate.
      skipTicks(ap, Infinity, autoWarpClockCap * autoWarpLeadS + (cmd.turnLeadS ?? 0) + frameDtS,
        budgetS, autoWarpClockCap < WARP_TOP_RATE ? WARP_SKIP_FRAME_MS : AUTO_WARP_FRAME_MS);
    } finally {
      gc.scrub(frameDtS);
    }
  }

  /**
   * Once a frame, after its own guidance step: the pace Auto-warp holds the clock
   * at, and a burn's watch pace, which holds with Auto-warp on or off. Between
   * burns the rate is the count to the next step over its lead, so
   * every approach closes at one steady zoom; in a burn it is the burn's length
   * over BURN_WATCH_REAL_S (a course fix BURN_WATCH_FIX_REAL_S) inside what the guidance steers at. Both ease.
   */
  function paceAutopilotClock(realDt: number): void {
    const cmd = autopilotCmd;
    autoWarpTickRate = 0;
    if (autopilot === null || cmd === null) { burnOpenT = null; autopilotPaceRate = 0; return; }
    const gc = gameClock();
    const burning = cmd.burnStepS > 0;
    if (!burning) burnOpenT = null;
    else if (burnOpenT === null) burnOpenT = gc.t;
    if (skipCarry !== null && (burning || cmd.nextStep !== skipCarry)) skipCarry = null;
    const driving = autoPaced();
    if ((!driving && (!burning || autopilotWarpWish !== null)) || gc.held || gc.paused || warp.pinned || inFight()) {
      autopilotPaceRate = 0;
      autoWarpClockCap = WARP_TOP_RATE;
      return;
    }
    const toStepS = !burning && cmd.nextStep !== null ? autopilotNextEventS(cmd) - (cmd.turnLeadS ?? 0) : null;
    let rate: number;
    if (burning) {
      rate = burnWatchEase(autopilotPaceRate > 0 ? autopilotPaceRate : gc.rate,
        burnWatchRate(gc.t - (burnOpenT ?? gc.t) + autopilotNextEventS(cmd), cmd.warpRate, cmd.burnSpanRate ?? cmd.warpCeiling,
          cmd.phase === 'trim' ? BURN_WATCH_FIX_REAL_S : BURN_WATCH_REAL_S),
        realDt);
    } else if (toStepS !== null && AUTO_WARP_NO_DROP) {
      rate = Math.max(WARP_TOP_RATE, autoWarpTargetRate(toStepS, cmd.warpRate));
    } else if (toStepS !== null && AUTO_WARP_CUT && cmd.nextStep === 'arrival') {
      // Climbs as Auto-warp does and never eases down: the drop lands in one frame.
      const was = autopilotPaceRate > 0 ? autopilotPaceRate : gc.rate;
      const up = Math.max(was, autoWarpEase(was, autoWarpTargetRate(toStepS, cmd.warpRate), realDt, null, cmd.warpRate));
      rate = toStepS > up * AUTO_WARP_MIN_LEAD_REAL_S ? up : cmd.warpRate;
    } else {
      // A course fix still being solved keeps the longer lead, so its solve lands in the frames it gives.
      const unsolved = cmd.nextStep === 'course-fix' && cmd.fixSolved !== true;
      autoWarpLeadS = cmd.nextStep === 'arrival' || unsolved ? AUTO_WARP_LEAD_REAL_S : AUTO_WARP_SHORT_LEAD_REAL_S;
      // Short of an unsolved fix the ladder's own rate carries the clock, so the solve lands before the date does.
      const aimS = toStepS === null ? null : unsolved && !SOLVE_AT_DATE ? toStepS - AUTO_WARP_FIX_HOLD_S : toStepS;
      const target = aimS === null ? cmd.warpRate : autoWarpTargetRate(aimS, cmd.warpRate, autoWarpLeadS);
      rate = autoWarpEase(autopilotPaceRate > 0 ? autopilotPaceRate : gc.rate, target, realDt, aimS, cmd.warpRate);
    }
    autopilotPaceRate = rate;
    autoWarpClockCap = cmd.phase === 'wait' && cmd.nextStep === 'window' && autoWarpDriving() && !AUTO_WARP_SLOW_WAIT
      ? AUTO_WARP_WAIT_CLOCK_RATE : WARP_TOP_RATE;
    warp.setAutopilotRate(Math.min(rate, autoWarpClockCap));
    if (driving && toStepS !== null && rate > autoWarpClockCap) autoWarpTickRate = rate - autoWarpClockCap;
  }

  /**
   * One guidance step of a burn: the cruise branch's own sim work at `dtSimS`
   * simulated seconds, in the order the frame loop runs it, with the autopilot's
   * own throttle and no pilot input. False where the trip has ended or a guidance
   * solve has opened and the ship must not move under it.
   *
   * The clock stands at the END of the step before it runs, the way it does for a
   * frame. The attitude turns on the step's own SIMULATED seconds and not on the
   * frame's real ones: the ship pivots at `flightConfig.turnRate` a second of its
   * own time, and a burn whose direction the orbit is turning is owed those
   * seconds whether the clock spends them in one real second or in sixty. A turn
   * to a new direction gets `realS`, the step's share of the frame's real seconds.
   */
  function burnStepTick(dtSimS: number, realS: number): boolean {
    const cmd = updateAutopilot(dtSimS, dtSimS, false, false, realS);
    if (cmd === null || cmd.solving === true || cmd.done) return false;
    updateFlightField();
    rebaseFlightFrame();
    advanceSimBodies();
    const carried = frameParentStep();
    simPos.advance(carried.x, carried.y, carried.z);
    refillFlightField();
    live.flightLastSubsteps = flightSubsteps(flightConfig, dtSimS, _field.pulls.length, false);
    live.flightLastSubstepS = live.flightLastSubsteps > 0 ? dtSimS / live.flightLastSubsteps : 0;
    _ground.contact.hit = false;
    // The same cap the frame's own step flies under, which is the cap the
    // autopilot was told it has.
    stepFlightFrame(cmd.axes, capThrottle(cmd.throttle, driveLimits(true).throttleCap), dtSimS, dtSimS,
      live.flightLastSubsteps, _step, 0);
    noteGroundContact();
    simPos.advance(_step.x, _step.y, _step.z);
    live.flightSimSeconds += dtSimS;
    return true;
  }

  /**
   * Fly all but the last of this frame's burn steps, and hand back the simulated
   * seconds the branch's own step is left with.
   *
   * A burn is re-aimed and re-integrated every `burnStepS` simulated seconds, and
   * the clock is offered what BURN_STEPS_PER_FRAME of those buy, so a frame that
   * runs longer than one step is flown as several rather than as one push along a
   * frozen nose. The step is read off the last frame's command: it is the same
   * burn in the same neighbourhood, and it is a bound rather than a deadline.
   */
  function runBurnSteps(dtSimS: number, realDt: number, stick: boolean): number {
    if (autopilot === null || stick || inFight()) return dtSimS;
    const stepS = autopilotCmd?.burnStepS ?? 0;
    if (!(stepS > 0) || !(dtSimS > stepS)) return dtSimS;
    const steps = Math.min(BURN_STEPS_PER_FRAME, Math.ceil(dtSimS / stepS));
    const sub = dtSimS / steps;
    gameClock().scrub(-dtSimS);
    for (let i = 1; i < steps; i++) {
      gameClock().scrub(sub);
      // The clock goes back with it: a step that did not fly leaves the ship and
      // the registry standing where the last one left them.
      if (!burnStepTick(sub, realDt / steps)) { gameClock().scrub(-sub); return 0; }
    }
    gameClock().scrub(sub);
    return sub;
  }

  /** The autopilot's own line for the panel, or null while it has never flown. */
  function autopilotFlightLine(): DvMapFlight | null {
    const cmd = autopilotCmd;
    if (cmd === null) return null;
    return {
      phase: cmd.phase,
      destinationName: autopilotDestName,
      dvToGoMps: cmd.dvToGoMps,
      nextEventS: autopilotNextEventS(cmd),
      trimSpentMps: cmd.trimSpentMps,
      spentMps: cmd.spentMps,
      pricedDvMps: (autopilotPlan?.totalDvKmS ?? 0) * 1000,
      reason: cmd.ended?.reason ?? null,
      ended: autopilot === null,
      skipStep: cmd.nextStep,
      nextStepLabel: cmd.nextStep === null ? '' : nextStepLabel(
        cmd.nextStep, cmd.nextEventS, autopilotPlan, tripAccelMps2(), gameClock().jdTdb(),
      ),
      skipSeconds: warpSkipSeconds(),
      skipping: warp.skipping,
      autoWarp: autoWarpStatus(),
      tripElapsedS: autopilot === null ? null : gameClock().t - tripStartT,
      tripPlannedS: autopilotPlan === null ? null : (autopilotPlan.arrivalEpochJd - tripStartJd) * 86400,
    };
  }

  // A trip's burns are priced at the thrust the autopilot will fly them at under
  // the radiators, so the map's burn lengths are the ones GO flies.
  const tripShare = (): number => deps.tripThrustShare?.() ?? 1;
  const tripAccelMps2 = (): number => flightConfig.accel * tripShare();

  // The delta-v map. It reads the ship off the bus above and the epoch off
  // `gameClock()`; only the registry comes in from here, so opening it costs no
  // second fetch of bodies.json.
  const dvMap = createDvMap({
    rows: () => live.bodyRows,
    stale: DVMAP_STALE,
    zeroMass: DVMAP_ZERO_MASS,
    burnNow: DVMAP_BURN_NOW,
    planeFromJ2000: DVMAP_PLANE_J2000,
    unsliced: DVMAP_UNSLICED,
    impulseDeparture: DVMAP_IMPULSE_DEPARTURE,
    resolveArmed: DVMAP_RESOLVES_ARMED,
    holdOnlyOnSurvey: DVMAP_HOLD_ONLY_ON_SURVEY,
    // A crossing the autopilot is flying is one the ship is ON: the map lets it
    // go at the departure like any other, and the moved row stays off there.
    underWay: () => autopilot !== null,
    shipAccelMps2: tripAccelMps2,
    arrivalOrbit: loadArrivalOrbit(),
    ignoreArrivalOrbit: GO_ORBIT_IGNORE,
  });
  const dvMapPanel = createDvMapPanel({
    map: dvMap,
    root: document.getElementById('dv-map-panel') as HTMLElement,
    flight: {
      go: (): void => { startAutopilot(); },
      cancel: cancelAutopilot,
      skip: startWarpSkip,
      autoWarp: { on: () => autoWarpOn, set: setAutoWarpOn },
    },
    accelMps2: () => flightConfig.accel,
    thrustShare: tripShare,
    rows: () => live.bodyRows,
  });
  // The COURSE FIX row and the burn marker: the autopilot's own trim solve, run
  // off the live ship state while nobody is flying, so a player flying the map's
  // plan by hand can read the correction Go reads. `live()` goes false the moment
  // GO takes the stick and the row goes quiet there, where the panel's own flight
  // rows say what the autopilot is doing; the PLAN is held through that, so a
  // player who cancels mid-transfer reads the fix for the trip they armed.
  const dvCorrection = createDvCorrection({
    plan: () => dvMap.selected(),
    live: () => autopilot === null && !inFight() && dvMap.state().mode === 'live',
    shipAt: () => {
      const w = shipWorldAt();
      return { x: w.x, y: w.y, z: w.z };
    },
    shipVelocity: () => {
      const v = shipWorldVelocity();
      return { x: v.x, y: v.y, z: v.z };
    },
    epochJd: () => gameClock().jdTdb(),
    accelMps2: () => flightConfig.accel,
    // The share GO's own departure would actually burn at, so the DEPARTURE
    // row's timing agrees with GO's under the same heat cap.
    burnThrustShare: () => driveLimits(true).burnThrustShare,
    env: autopilotEnv,
    off: DV_CORRECTION_OFF,
    holdOutlived: DV_CORRECTION_HOLD_OUTLIVED,
    holdByArrival: DV_CORRECTION_HOLD_BY_ARRIVAL,
    handedOn: () => dvMap.moved(),
  });

  // Read by `tools/preview/descent-dojo.mjs --dvmap`, beside `simFlight`. The
  // scrub is the arm's own way to move the epoch by months without waiting for
  // the clock to run there; nothing a player presses reaches it.
  scene.userData['simDvMap'] = {
    open(): void { dvMap.setOpen(true); },
    close(): void { dvMap.setOpen(false); },
    isOpen(): boolean { return dvMap.isOpen(); },
    setMode(mode: 'live' | 'parking'): void { dvMap.setMode(mode); },
    select(id: string | null): void { dvMap.select(id); },
    replan(): void { dvMap.replan(); },
    scrubDays(days: number): void { gameClock().scrub(days * 86400); },
    state(): {
      open: boolean; mode: string; epochJd: number; planMs: number; plans: number;
      originLabel: string; originAltitudeKm: number | null; reason: string | null;
      selectedId: string | null;
      departurePricing: string | null; planned: number; skipped: number;
      busy: boolean; working: number; sliceMaxMs: number; slices: number; solveMs: number;
      selectedDepartureJd: number | null; selectedArrivalJd: number | null;
      selectedDvKmS: number | null; selectedTiltDeg: number | null;
      arrivalOrbit: string; arrivalRepricing: boolean; selectedCaptureAltitudeKm: number | null;
    } {
      const s = dvMap.state();
      return {
        open: s.open, mode: s.mode, epochJd: s.epochJd, planMs: s.planMs, plans: s.plans,
        busy: s.busy, working: s.working.length, sliceMaxMs: s.sliceMaxMs, slices: s.slices,
        solveMs: s.solveMs,
        originLabel: s.originLabel, originAltitudeKm: s.originAltitudeKm,
        // The panel's own note under the FROM line, which is where a map with no
        // survey says why, and where a crossing let go out there is said at all.
        reason: s.reason,
        selectedId: s.selectedId,
        departurePricing: s.survey?.departurePricing ?? null,
        planned: s.survey?.plans.length ?? 0,
        skipped: s.survey?.skipped.length ?? 0,
        // The crossing GO would take, which is the one armed when the row was
        // picked while it can still be flown.
        selectedDepartureJd: s.selected?.departureEpochJd ?? null,
        selectedArrivalJd: s.selected?.arrivalEpochJd ?? null,
        selectedDvKmS: s.selected?.totalDvKmS ?? null,
        // A plan made in another body's frame, which the panel offers no GO for:
        // the press trace prints it beside the box it could not press.
        selectedTiltDeg: s.selected?.planeTiltDeg ?? null,
        arrivalOrbit: s.arrivalOrbit,
        arrivalRepricing: s.arrivalRepricing,
        selectedCaptureAltitudeKm: s.selected?.captureAltitudeKm ?? null,
      };
    },
    /** The picked trip's flight plan as the panel paints it, which
     *  tools/preview/dvmap-panel-fit.mjs reads both drawn lists against. */
    flightPlan(): {
      selectedId: string; totalDvKmS: number;
      steps: Array<{ kind: string; label: string; atS: number; dvMps: number | null; durationS: number }>;
    } | null {
      const s = dvMap.state();
      if (s.selected === null || s.selectedId === null) return null;
      return {
        selectedId: s.selectedId,
        totalDvKmS: s.selected.totalDvKmS,
        steps: planRows(s.selected, flightConfig.accel).map((row) => ({
          kind: row.kind, label: row.label, atS: row.atS, dvMps: row.dvMps, durationS: row.durationS,
        })),
      };
    },
    /** The moved row as the panel writes it, beside the two departures behind it:
     *  null while the crossing the row was picked on still stands. */
    moved(): {
      text: string; armedDepartureJd: number; departureJd: number; dvChangeKmS: number;
    } | null {
      const m = dvMap.state().moved;
      return m === null ? null : {
        text: movedRowText(m), armedDepartureJd: m.armedDepartureJd,
        departureJd: m.departureJd, dvChangeKmS: m.dvChangeKmS,
      };
    },
    /** The COURSE FIX row as the panel writes it, and the marker's own direction:
     *  what a player reads, for the `--transit` arm's own leg. */
    correction(): {
      text: string; mps: number; armed: boolean; pending: boolean; answers: number;
      solving: boolean; markerKind: string | null; seedJd: number; missM: number;
      timeToGoS: number;
    } {
      const s = dvCorrection.state();
      return {
        text: correctionRowText(s), mps: s.mps, armed: s.armed, pending: s.pending,
        answers: s.answers, solving: s.solving, markerKind: s.marker?.kind ?? null,
        seedJd: s.seedJd, missM: s.missM, timeToGoS: s.timeToGoS,
      };
    },
    dispose(): void {
      cancelAutopilot();
      dvMapPanel.dispose();
      dvCorrection.dispose();
      dvMap.dispose();
    },
  };

  // Read by the transit arm. Everything here reads what the autopilot last said,
  // except `stick`, the one synthetic pilot input the arm needs.
  scene.userData['simAutopilot'] = {
    /** Its own phase word, or null while nothing has ever flown. */
    get phase(): string | null { return autopilotCmd?.phase ?? null; },
    /** True while it still has the stick. */
    get flying(): boolean { return autopilot !== null; },
    get throttle(): number { return autopilotCmd?.throttle ?? 0; },
    /** What was actually handed to `stepFlight` last cruise frame. */
    get appliedThrottle(): number { return cruiseThrottleApplied; },
    get dvToGoMps(): number { return autopilotCmd?.dvToGoMps ?? 0; },
    get nextEventS(): number { return autopilotCmd === null ? 0 : autopilotNextEventS(autopilotCmd); },
    /** The step that count is to, and the seconds the skip would take off it. */
    get nextStep(): string | null { return autopilotCmd?.nextStep ?? null; },
    get skipSeconds(): number { return warpSkipSeconds(); },
    /** True while a skip is still running its ticks. */
    get skipping(): boolean { return warp.skipping; },
    /** Widest the nose stood off its direction at the start of any of the last
     *  frame's steps: under a degree or two the frame followed a moving burn
     *  direction, past that it turned to a new one. burn-slew-check reads it. */
    get frameNoseErrorDeg(): number { return autopilotFrameNoseErrorRad * 180 / Math.PI; },
    /** Real milliseconds inside the frame loop the last skip spent, and the ticks
     *  it spent them on: the cost of the skip itself, apart from the frames the
     *  page spent painting between them, and the slowest one frame spent. */
    get skipMs(): number { return warp.skipMs; },
    get skipTicks(): number { return warp.skipTicks; },
    get skipFrameMaxMs(): number { return warpSkipFrameMaxMs; },
    /** Press WARP TO the next step, with no button to click. */
    skip(): void { startWarpSkip(); },
    get trimSpentMps(): number { return autopilotCmd?.trimSpentMps ?? 0; },
    /** Every burn's spend so far, beside what the map priced the whole trip at. */
    get spentMps(): number { return autopilotCmd?.spentMps ?? 0; },
    get pricedDvMps(): number { return (autopilotPlan?.totalDvKmS ?? 0) * 1000; },
    /** The crossing being flown, as GO took it off the map: a check comparing it
     *  against the row that was picked reads whether the two are the same trip. */
    get planDepartureJd(): number { return autopilotPlan?.departureEpochJd ?? 0; },
    get planArrivalJd(): number { return autopilotPlan?.arrivalEpochJd ?? 0; },
    /** The plan's burns and the circle its capture parks on, and the epoch the
     *  capture's throttle is due to open: autowarp-check's burn and arrival bars. */
    get planBurns(): { kind: string; epochJd: number; dvMps: number }[] | null {
      return autopilotPlan?.burns.map((b) => ({ kind: b.kind, epochJd: b.epochJd, dvMps: b.dvKmS * 1000 })) ?? null;
    },
    get planParkingRadiusKm(): number | null {
      return autopilotPlan?.burns.find((b) => b.kind === 'capture')?.finiteCapture?.parkingRadiusKm ?? null;
    },
    /** Metres the last course fix solved ahead of its date had the ship off. */
    get aheadOffM(): number | null {
      const off = autopilot?.aheadOffM ?? NaN;
      return Number.isFinite(off) ? off : null;
    },
    get captureDueJd(): number | null {
      const due = autopilot?.captureDueJd() ?? NaN;
      return Number.isFinite(due) ? due : null;
    },
    get warpRate(): number { return autopilotCmd?.warpRate ?? 0; },
    /** Simulated seconds one guidance step of the burn in hand covers, 0 outside
     *  one, and the rate the player asked the clock for mid-flight. */
    get burnStepS(): number { return autopilotCmd?.burnStepS ?? 0; },
    get warpWish(): number | null { return autopilotWarpWish; },
    /** Auto-warp on the trip in hand ('driving', 'stood down', 'off'), null with
     *  none flying; and TRIP TIME's zero and count, null once the trip is over. */
    get autoWarp(): string | null { return autopilot === null ? null : autoWarpStatus(); },
    get tripStartJd(): number | null { return autopilot === null ? null : tripStartJd; },
    get tripElapsedS(): number | null { return autopilot === null ? null : gameClock().t - tripStartT; },
    get reason(): string | null { return autopilotCmd?.ended?.reason ?? null; },
    /** Longest `update()` call on a trim rung, ms, and the frame that carried it. */
    get trimUpdateMaxMs(): number { return autopilotTrimUpdateMaxMs; },
    get trimFrameMaxMs(): number { return autopilotTrimFrameMaxMs; },
    get trimFrames(): number { return autopilotTrimFrames; },
    /** Longest `update()` call of the flight, ms, and the frames a solve was
     *  spread over: with the solve sliced the two together are the frame cost. */
    get updateMaxMs(): number { return autopilotUpdateMaxMs; },
    get solveFrames(): number { return autopilotSolveFrames; },
    /** Integrator steps one `update()` spends on a solve; 0 runs it whole. */
    get solveStepBudget(): number { return AUTOPILOT_UNSLICED_SOLVE ? 0 : SOLVE_STEP_BUDGET; },
    /** The clock the autopilot reads, for a check measuring its own scrubs. */
    get jd(): number { return gameClock().jdTdb(); },
    /** A control touched, with no key to press. */
    stick(): void { notePilotInput(); },
    cancel(): void { cancelAutopilot(); },
  };

  function fly(realDt: number, dt: number, t: number): void {
    const inp = input.state;
    // Control only: the behaviour --leg target's reading says must not exist.
    if (TARGET_DRIVES_GO && autopilot !== null && autopilotPlan !== null) {
      const picked = gameState().ship.flight.flightTargetId;
      if (picked !== null && picked !== autopilotPlan.destinationId) cancelAutopilot();
    }
    // ----- Hand flight -----
    // Held axes and a held throttle; a release leaves the velocity alone and
    // the ship coasts. `dt` is SIM seconds, so the warp control owns the coast.
    // The player's own rotation runs on REAL seconds instead (`realDt`, above),
    // so a tap or a drag turns the ship the same amount whatever the clock is
    // doing; `?attitudesimdt=1` is the dev-only variant that puts it back on
    // `dt` for the check that has to catch the pre-fix bug.
    const held = gameState().ship.flight.held;
    if (inp.allStop) setHeldCruiseThrottle(0);
    // W/S override the bar's held level for as long as they are pressed.
    const barThrottle = inp.throttleKey ? inp.throttle : gameState().ship.flight.heldCruiseThrottle;
    // The delta-v map's Go, before anything else this frame reads the axes: it
    // owns attitude and throttle until a control is touched.
    const rawStick = stickTouched();
    if (!rawStick) autopilotStickHeld = false;
    // The skip's own ticks first, so this frame's own tick carries on from where
    // they left the clock rather than from where the last frame ended.
    autopilotFrameNoseErrorRad = 0;
    runWarpSkip(rawStick && !autopilotStickHeld, dt);
    runAutoWarpTicks(rawStick && !autopilotStickHeld, dt, realDt);
    // A burn's leading guidance steps next, so what this frame draws and reports
    // is the last of them: `dtFly` is the seconds they left it.
    const burnFrame = autopilot !== null && !(rawStick && !autopilotStickHeld) && !inFight()
      && (autopilotCmd?.burnStepS ?? 0) > 0;
    const dtFly = runBurnSteps(dt, realDt, rawStick && !autopilotStickHeld);
    // A burn's step, split or not, gets its simulated seconds of turn. On the
    // real second the frame runs on, the nose could not come round the degree
    // the direction has moved and every other step would slew instead of burning.
    const burnSplit = dtFly !== dt || burnFrame;
    const cmd = updateAutopilot(dtFly, burnSplit ? dtFly : realDt,
      rawStick && !autopilotStickHeld, true, burnSplit && dt > 0 ? realDt * dtFly / dt : realDt);
    paceAutopilotClock(realDt);
    const axes: FlightAxes = cmd !== null ? cmd.axes : (held ?? inp);
    const commanded = cmd !== null ? cmd.throttle : (held ? held.throttle : barThrottle);
    // Whatever cap the drive's owner sets, on cruise's own [-1, 1] scale; in play
    // that is the fight branch's thermal cap, and on the autopilot's burn the
    // share it was planned on.
    const throttle = capThrottle(commanded, driveLimits(cmd !== null).throttleCap);
    cruiseThrottleApplied = throttle;
    // The pick FIRST, at the configuration the frame STARTS in: the registry
    // still stands where the last frame left it and so does the ship, so the
    // body pulling hardest is decided against one epoch rather than against a
    // ship carried by one parent and bodies placed for another.
    if (!FLIGHT_CARRY_OLD_PARENT) {
      updateFlightField();
      rebaseFlightFrame();
    }
    // The registry, at the epoch the clock has just advanced to: the pulls, the
    // frame the ship's velocity is in, and the world the Atlas draws are all
    // this one placement.
    advanceSimBodies();
    // The carry, on the parent the interval is about to be flown in. The
    // registry has just moved to this frame's epoch; a ship still standing at
    // the last one is a whole frame of the parent's travel out of position, and
    // at 120x that is 48 km against a 3,700 km orbit - a systematic pull along
    // the parent's own track that decays the orbit by kilometres a revolution.
    const carried = frameParentStep();
    simPos.advance(carried.x, carried.y, carried.z);
    // The pulls at this frame's own epoch, the pick above left standing.
    if (FLIGHT_CARRY_OLD_PARENT) {
      updateFlightField();
      rebaseFlightFrame();
    } else {
      refillFlightField();
    }
    live.flightLastSubsteps = flightSubsteps(flightConfig, dtFly, _field.pulls.length, assistLive());
    live.flightLastSubstepS = live.flightLastSubsteps > 0 ? dtFly / live.flightLastSubsteps : 0;
    const attitudeDt = FLIGHT_ATTITUDE_SIM_DT || burnSplit ? dtFly : realDt;
    const dvBefore = flight.deltaVSpent;
    // Cleared here rather than inside the step: one frame is flown as several
    // `stepFlight` calls and a contact must survive the spans after it.
    _ground.contact.hit = false;
    noteStopLateSag(dtFly);
    stepFlightFrame(axes, throttle, dtFly, attitudeDt, live.flightLastSubsteps, _step,
      assistSeconds(dtFly, realDt));
    noteGroundContact();
    // The slots back at this frame's own epoch before anything reads them: the
    // prediction's near end, the pilot rows and the warp envelope all label
    // what the frame DREW, not where a span's midpoint put a body.
    refillFlightField();
    _thrustDrive.cruise = true;
    _thrustDrive.throttle = throttle;
    _thrustDrive.lateralDvMps = thrustLateral(flight.deltaVSpent - dvBefore, throttle, dtFly);
    _thrustDrive.realDt = realDt;
    _thrustDrive.pushBody.x = _thrustPush.x;
    _thrustDrive.pushBody.y = _thrustPush.y;
    _thrustDrive.pushBody.z = _thrustPush.z;
    live.flightSimSeconds += dtFly;
    // `_step` is the ship's motion RELATIVE to the frame's parent; the parent's
    // own travel over the same interval was carried before the step.
    simPos.advance(_step.x, _step.y, _step.z);
    if (pathRecorder.armed) notePathRow();
    // The Atlas's bodies at this frame's epoch before the line reads their
    // ground; the step's own sweep above reads them where the frame began.
    const aw = LINE_EPOCH_LATE ? null : atlasWorld();
    if (aw) aw.setWorldEpoch(Number.isFinite(live.seedJd) ? live.seedJd : null);
    // After the advance, so the line starts where the ship now is. The pulls
    // are this tick's own set and the bodies do not move between ticks, so the
    // prediction integrates against exactly what the next tick will.
    trajectory.update({
      nowMs: t * 1000,
      state: flight,
      at: shipWorldAt(),
      pulls: _field.pulls,
      pullIds: _pullIds,
      pullRadiiM: _pullRadii,
      surfaceAt: _ground.surfaceAt,
      pullOuterRadiiM: _ground.outerRadiiM,
      pullPeriodS: _pullPeriodS,
      pullParentIdx: _pullParentIdx,
      framePullIdx: gravityField.frameParentIdx,
      sampleAt: BODIES_PINNED ? null : sampleTrajectoryBody,
      axes,
      throttle,
      rate: gameClock().rate,
      assist: assistLive(),
      simSeconds: live.flightSimSeconds,
    });
    syncShipTransform();
    // The state is heliocentric; the ship is drawn in the bubble's own metres.
    ship.group.quaternion.copy(_invBubble).multiply(
      _flightQuat.set(flight.orientation.x, flight.orientation.y,
        flight.orientation.z, flight.orientation.w),
    );
    thrusters.update(_thrustDrive);
    ship.update(dtFly, t);
  }

  return {
    fly,
    notePilotInput,
    cancelAutopilot,
    requestAutopilotWarp,
    autopilotFlightLine,
    dvMap,
    dvMapPanel,
    dvCorrection,
    get autopilot() { return autopilot; },
    get autopilotCmd() { return autopilotCmd; },
    get autopilotDueT(): number | null {
      const cmd = autopilotCmd;
      if (autopilot === null || cmd === null || cmd.done) return null;
      if (cmd.nextStep === null || cmd.throttle !== 0) return autopilotCmdAtT;
      return autopilotCmdAtT + autopilotNextEventS(cmd);
    },
    get autopilotWarpWish() { return autopilotWarpWish; },
    set autopilotWarpWish(v: number | null) { autopilotWarpWish = v; },
    get autopilotAssistWas() { return autopilotAssistWas; },
    set autopilotAssistWas(v: boolean) { autopilotAssistWas = v; },
    get throttleApplied() { return cruiseThrottleApplied; },
    dispose(): void {
      window.removeEventListener('pagehide', onPageHide);
      cancelAutopilot();
      dvMapPanel.dispose();
      dvCorrection.dispose();
      dvMap.dispose();
    },
  };
}
